import { useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSource } from "@/context/SourceContext";
import { useAuth } from "@/context/AuthContext";
import { useNav } from "@/components/DashboardShell";
import PageHeader from "@/components/PageHeader";
import StatCard, { CountUp } from "@/components/StatCard";
import EmptyState from "@/components/EmptyState";
import ListSkeleton from "@/components/ListSkeleton";
import TrackingPill from "@/components/TrackingPill";
import { celebrate, triggerHaptic } from "@/lib/haptics";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import StatusBadge, { STATUS_META, STATUS_OPTIONS } from "@/components/StatusBadge";
import Milestones from "@/components/Milestones";
import PortalBadge, { Route } from "@/components/PortalBadge";
import BookingDialog from "@/components/BookingDialog";
import BatchesPanel from "@/components/BatchesPanel";
import Timeline from "@/components/Timeline";
import { InvoiceView, AwbLabel, ProformaInvoice, PodDocument } from "@/components/ShipmentDocs";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
  PieChart, Pie,
} from "recharts";
import {
  Package, Truck, CheckCircle2, AlertTriangle, Users, Search, Loader2, UserCog,
  IndianRupee, Radio, Plus, Copy, Globe, Trash2, Coins, Upload, MapPin,
  MoreHorizontal, CalendarClock, FileText, ScanLine, Download, ArrowLeft, X, ShieldCheck, Camera,
} from "lucide-react";
import { toast } from "@/lib/toast";

const PORTAL_CHART_COLORS = ["#2563EB", "#E11D48", "#10B981", "#F97316", "#8B5CF6"];
const RATE_COUNTRIES = ["India", "South Korea", "Japan", "UAE", "United States", "United Kingdom", "Singapore", "Australia", "Canada", "Germany", "Malaysia", "Thailand", "Domestic"];
const RATE_SERVICES = ["DDP"];

const STATUS_COLORS = {
  CREATED: "#FFB800", PICKED_UP: "#8B5CF6", IN_TRANSIT: "#22D3EE",
  OUT_FOR_DELIVERY: "#3B82F6", DELIVERED: "#10B981", CANCELLED: "#F43F5E",
};

const SECTION_META = {
  overview: { eyebrow: "Command Center", title: "Operations Overview", subtitle: "Live pulse across every source portal — revenue, volume, delivery health." },
  orders: { eyebrow: "Shipments", title: "Orders", subtitle: "Search, assign, update and print — every shipment in one table." },
  warehouse: { eyebrow: "New Delhi Hub", title: "Warehouse", subtitle: "Scan every box to receive shipments at the hub, then look up and print documents." },
  portals: { eyebrow: "Network", title: "Source Portals", subtitle: "Country portals feeding bookings into ShipOMS — branding, AWB series and rates." },
  rates: { eyebrow: "Pricing", title: "Rate Cards", subtitle: "Country-wise & weight-wise pricing used for automatic quotes." },
  users: { eyebrow: "Access", title: "Users & Couriers", subtitle: "Manage accounts and roles across the platform." },
};

export default function AdminDashboard() {
  const { section } = useNav();
  const m = SECTION_META[section];
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {m && <PageHeader eyebrow={m.eyebrow} title={m.title} subtitle={m.subtitle} />}
      {section === "overview" && <Overview />}
      {section === "orders" && <OrdersPanel />}
      {section === "batches" && <BatchesPanel />}
      {section === "warehouse" && <WarehousePanel />}
      {section === "portals" && <PortalsPanel />}
      {section === "rates" && <RatesPanel />}
      {section === "users" && <UsersPanel />}
    </div>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const kpis = [
    { label: "Total Orders", value: data.total_orders, icon: Package, tone: "blue" },
    { label: "Active", value: data.active_orders, icon: Truck, tone: "cyan" },
    { label: "Delivered", value: data.delivered, icon: CheckCircle2, tone: "emerald" },
    { label: "Unassigned", value: data.unassigned, icon: AlertTriangle, tone: "amber" },
  ];
  const statusData = Object.entries(data.status_counts).map(([k, v]) => ({ name: STATUS_META[k]?.label || k, key: k, value: v }));
  const portalData = (data.by_portal || []).map((p) => ({ name: p.name.replace(".in", ""), orders: p.orders, revenue: p.revenue }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="panel spotlight fade-up relative col-span-2 overflow-hidden p-5" data-testid="kpi-revenue">
          <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 left-1/3 h-32 w-32 rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="relative flex items-center justify-between">
            <div className="text-[11px] font-mono-label font-semibold uppercase tracking-[0.16em] text-primary">Total Revenue · all portals</div>
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20"><IndianRupee className="h-4 w-4" /></span>
          </div>
          <div className="relative mt-3 font-display text-4xl font-extrabold tracking-tight text-primary"><CountUp value={data.revenue || 0} prefix="₹" /></div>
          <div className="relative mt-1 text-xs text-muted-foreground">Across {data.total_portals} connected source portal{data.total_portals === 1 ? "" : "s"}</div>
        </div>
        <StatCard label="Delivery Rate" value={data.delivery_rate} suffix="%" decimals={1} icon={CheckCircle2} tone="emerald" delay={1} testId="kpi-delivery-rate" />
        <StatCard label="Portals" value={data.total_portals} icon={Radio} tone="orange" delay={2} testId="kpi-portals" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => <StatCard key={k.label} label={k.label} value={k.value} icon={k.icon} tone={k.tone} delay={i + 3} testId={`kpi-${k.label.toLowerCase().replace(/ /g, "-")}`} />)}
      </div>

      <div className="panel p-5">
        <h3 className="font-display text-xl font-bold tracking-tight mb-1">Channel Performance</h3>
        <p className="text-sm text-muted-foreground mb-4">Orders ingested from each connected portal</p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={portalData}>
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} />
            <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, boxShadow: "0 8px 24px -8px rgba(30,58,138,0.15)" }} />
            <Bar dataKey="orders" radius={[6, 6, 0, 0]}>
              {portalData.map((e, i) => <Cell key={i} fill={PORTAL_CHART_COLORS[i % PORTAL_CHART_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {(data.by_portal || []).map((p, i) => (
            <div key={p.name} className="rounded-lg border border-border bg-secondary/40 p-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: PORTAL_CHART_COLORS[i % PORTAL_CHART_COLORS.length] }}>
                <span className="h-2 w-2 rounded-full" style={{ background: PORTAL_CHART_COLORS[i % PORTAL_CHART_COLORS.length] }} /> {p.name}
              </div>
              <div className="text-sm mt-1">{p.orders} order{p.orders === 1 ? "" : "s"}</div>
              <div className="text-xs text-muted-foreground">₹{p.revenue.toLocaleString("en-IN")}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="panel p-5">
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">Orders by Status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statusData}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94A3B8" }} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, boxShadow: "0 8px 24px -8px rgba(30,58,138,0.15)" }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {statusData.map((e) => <Cell key={e.key} fill={STATUS_COLORS[e.key] || "#64748B"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="panel p-5">
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">Courier Workload</h3>
          {data.workload.length === 0 ? (
            <div className="text-muted-foreground text-sm py-10 text-center">No couriers yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.workload} dataKey="active_orders" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                  {data.workload.map((e, i) => <Cell key={i} fill={["#FF6B00", "#22D3EE", "#10B981", "#8B5CF6", "#FFB800"][i % 5]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, boxShadow: "0 8px 24px -8px rgba(30,58,138,0.15)" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <StatCard label="Customers" value={data.total_users} icon={Users} tone="cyan" testId="kpi-customers" />
        <StatCard label="Couriers" value={data.total_employees} icon={Truck} tone="orange" testId="kpi-couriers" />
      </div>
    </div>
  );
}

function PortalsPanel() {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const EMPTY = { name: "", domain: "", origin_country: "India", destination_country: "", status: "active", company_name: "", company_address: "", gstin: "", invoice_prefix: "INV", awb_prefix: "AWB", awb_counter: "1000", logo_file_id: "" };
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [rateOpen, setRateOpen] = useState(false);
  const [ratePortal, setRatePortal] = useState(null);
  const [rateForm, setRateForm] = useState({ id: null, service_type: "DDP", per_kg_extra: "", slabs: [{ min_weight: "0", max_weight: "1", price: "" }] });
  const [rateSaving, setRateSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portals");
      setPortals(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, domain: p.domain, origin_country: p.origin_country, destination_country: p.destination_country, status: p.status, company_name: p.company_name || "", company_address: p.company_address || "", gstin: p.gstin || "", invoice_prefix: p.invoice_prefix || "INV", awb_prefix: p.awb_prefix || "AWB", awb_counter: String(p.awb_counter ?? "1000"), logo_file_id: p.logo_file_id || "" }); setOpen(true); };

  const uploadLogo = async (fileList) => {
    const f = (fileList || [])[0];
    if (!f) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((fm) => ({ ...fm, logo_file_id: data.id }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploadingLogo(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, awb_counter: parseInt(form.awb_counter, 10) || 1000 };
      if (editing) {
        await api.patch(`/portals/${editing.id}`, payload);
        toast.success("Portal updated");
      } else {
        await api.post("/portals", payload);
        toast.success("Portal added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (p) => {
    try {
      await api.patch(`/portals/${p.id}`, { status: p.status === "active" ? "inactive" : "active" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyKey = (k) => { navigator.clipboard?.writeText(k); toast.success("Intake API key copied"); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openRates = async (p) => {
    setRatePortal(p);
    try {
      const { data } = await api.get("/rates");
      const match = data.find((r) => r.destination_country === p.destination_country);
      if (match) {
        setRateForm({ id: match.id, service_type: match.service_type, per_kg_extra: String(match.per_kg_extra ?? ""), slabs: match.slabs.map((s) => ({ min_weight: String(s.min_weight), max_weight: String(s.max_weight), price: String(s.price) })) });
      } else {
        setRateForm({ id: null, service_type: "DDP", per_kg_extra: "", slabs: [{ min_weight: "0", max_weight: "1", price: "" }] });
      }
    } catch {
      setRateForm({ id: null, service_type: "DDP", per_kg_extra: "", slabs: [{ min_weight: "0", max_weight: "1", price: "" }] });
    }
    setRateOpen(true);
  };
  const setRateSlab = (i, k) => (e) => setRateForm((f) => ({ ...f, slabs: f.slabs.map((s, idx) => (idx === i ? { ...s, [k]: e.target.value } : s)) }));
  const addRateSlab = () => setRateForm((f) => ({ ...f, slabs: [...f.slabs, { min_weight: "", max_weight: "", price: "" }] }));
  const removeRateSlab = (i) => setRateForm((f) => ({ ...f, slabs: f.slabs.length > 1 ? f.slabs.filter((_, idx) => idx !== i) : f.slabs }));
  const saveRates = async (e) => {
    e.preventDefault();
    setRateSaving(true);
    try {
      const slabs = rateForm.slabs.map((s) => ({ min_weight: parseFloat(s.min_weight) || 0, max_weight: parseFloat(s.max_weight) || 0, price: parseFloat(s.price) || 0 }));
      const per = parseFloat(rateForm.per_kg_extra) || 0;
      if (rateForm.id) {
        await api.patch(`/rates/${rateForm.id}`, { slabs, per_kg_extra: per });
      } else {
        await api.post("/rates", { destination_country: ratePortal.destination_country, service_type: rateForm.service_type || "DDP", slabs, per_kg_extra: per });
      }
      toast.success("Rates saved");
      setRateOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRateSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="portals-panel">
      <div className="flex items-center justify-end">
        <Button onClick={openNew} className="shine-hover" data-testid="add-portal-button"><Plus className="h-4 w-4" /> Add Portal</Button>
      </div>

      {loading ? (
        <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {portals.map((p) => (
            <div key={p.id} className="panel hover-lift p-5" data-testid={`portal-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {p.logo_file_id ? (
                    <img src={`${API}/portal-logo/${p.logo_file_id}`} alt={p.name} className="h-10 w-10 rounded-lg object-contain border border-border bg-white" />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Globe className="h-5 w-5" /></div>
                  )}
                  <div>
                    <div className="font-display text-lg font-bold">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.domain}</div>
                  </div>
                </div>
                <button onClick={() => toggleStatus(p)} data-testid={`toggle-portal-${p.id}`}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${p.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                  {p.status}
                </button>
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm">
                <Route from={p.origin_country} to={p.destination_country} />
                <span className="text-muted-foreground">· {p.order_count} orders</span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded bg-secondary/50 px-2 py-1"><span className="text-muted-foreground">AWB series</span> <span className="font-semibold">{p.awb_prefix}{String(p.awb_counter || 0).padStart(8, "0")}</span></div>
                <div className="rounded bg-secondary/50 px-2 py-1 truncate"><span className="text-muted-foreground">Invoice</span> <span className="font-semibold">{p.invoice_prefix}</span></div>
              </div>
              {p.company_name && <div className="mt-2 text-xs text-muted-foreground">{p.company_name}{p.gstin ? ` · GSTIN ${p.gstin}` : ""}</div>}
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-secondary/50 px-3 py-2">
                <code className="text-xs truncate flex-1">{p.api_key}</code>
                <Button size="sm" variant="ghost" onClick={() => copyKey(p.api_key)} data-testid={`copy-key-${p.id}`}><Copy className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-portal-${p.id}`}>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => openRates(p)} data-testid={`manage-rates-${p.id}`}><Coins className="h-4 w-4 mr-1" /> Rates</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-border bg-secondary/30 p-5">
        <h4 className="font-display font-bold mb-2">Portal Intake API</h4>
        <p className="text-sm text-muted-foreground mb-2">Each portal POSTs bookings to ShipOMS using its key:</p>
        <pre className="text-xs bg-white border border-border rounded-lg p-3 overflow-x-auto">{`POST /api/intake/booking
X-Portal-Key: <portal api key>
{
  "customer_name": "Priya S",
  "customer_phone": "+91 98765 43210",
  "pickup_address": "Mumbai, IN",
  "delivery_address": "Seoul, KR",
  "recipient_name": "Ji-woo Kim",
  "recipient_phone": "+82 10 1234 5678",
  "package_type": "Cosmetics",
  "weight": 2.5,
  "service_type": "DDP",
  "quoted_price": 1870
}`}</pre>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="portal-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{editing ? "Edit Portal" : "Add Portal"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="portal-form">
            <div className="space-y-2">
              <Label>Portal Name</Label>
              <Input value={form.name} onChange={set("name")} placeholder="ShipVietnam.in" required data-testid="portal-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Domain</Label>
              <Input value={form.domain} onChange={set("domain")} placeholder="shipvietnam.in" required data-testid="portal-domain-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Origin Country</Label>
                <Input value={form.origin_country} onChange={set("origin_country")} required data-testid="portal-origin-input" />
              </div>
              <div className="space-y-2">
                <Label>Destination Country</Label>
                <Input value={form.destination_country} onChange={set("destination_country")} placeholder="Vietnam" required data-testid="portal-dest-input" />
              </div>
            </div>
            <div className="border-t border-border pt-3 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Invoice Branding &amp; AWB Series</div>
              <div className="space-y-2">
                <Label>Portal Logo <span className="text-muted-foreground font-normal">(shown on invoice, AWB & tracking)</span></Label>
                <div className="flex items-center gap-3">
                  {form.logo_file_id && <img src={`${API}/portal-logo/${form.logo_file_id}`} alt="logo" className="h-12 w-12 rounded-lg object-contain border border-border bg-white" data-testid="portal-logo-preview" />}
                  <label className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 cursor-pointer hover:bg-secondary/40 transition-colors" data-testid="portal-logo-label">
                    {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    <span className="text-sm">{form.logo_file_id ? "Replace logo" : "Upload logo"}</span>
                    <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" disabled={uploadingLogo}
                      onChange={(e) => { uploadLogo(e.target.files); e.target.value = ""; }} data-testid="portal-logo-input" />
                  </label>
                  {form.logo_file_id && <Button type="button" size="sm" variant="ghost" className="text-rose-600" onClick={() => setForm((f) => ({ ...f, logo_file_id: "" }))} data-testid="portal-logo-remove">Remove</Button>}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Company Name (on invoice)</Label>
                <Input value={form.company_name} onChange={set("company_name")} placeholder="ShipVietnam Logistics Pvt Ltd" data-testid="portal-company-input" />
              </div>
              <div className="space-y-2">
                <Label>Company Address</Label>
                <Input value={form.company_address} onChange={set("company_address")} placeholder="Andheri East, Mumbai 400069, India" data-testid="portal-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>GSTIN</Label>
                  <Input value={form.gstin} onChange={set("gstin")} placeholder="27AABCS1234K1Z5" data-testid="portal-gstin-input" />
                </div>
                <div className="space-y-2">
                  <Label>Invoice Prefix</Label>
                  <Input value={form.invoice_prefix} onChange={set("invoice_prefix")} placeholder="SV-INV" data-testid="portal-invprefix-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>AWB Prefix</Label>
                  <Input value={form.awb_prefix} onChange={set("awb_prefix")} placeholder="SV" data-testid="portal-awbprefix-input" />
                </div>
                <div className="space-y-2">
                  <Label>AWB Start No.</Label>
                  <Input type="number" value={form.awb_counter} onChange={set("awb_counter")} placeholder="1000" data-testid="portal-awbcounter-input" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="portal-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="save-portal-button">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing ? "Save Changes" : "Add Portal"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="portal-rate-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Rates · {ratePortal?.destination_country}</DialogTitle></DialogHeader>
          <form onSubmit={saveRates} className="space-y-4" data-testid="portal-rate-form">
            <p className="text-sm text-muted-foreground">Weight-wise pricing for shipments to <span className="font-semibold text-foreground">{ratePortal?.destination_country}</span> via {ratePortal?.name}.</p>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Weight Slabs</Label>
                <Button type="button" size="sm" variant="outline" onClick={addRateSlab} data-testid="portal-add-slab-button"><Plus className="h-4 w-4 mr-1" /> Slab</Button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1">
                  <span className="col-span-4">Min kg</span><span className="col-span-4">Max kg</span><span className="col-span-3">Price ₹</span><span className="col-span-1" />
                </div>
                {rateForm.slabs.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`portal-slab-${i}`}>
                    <Input className="col-span-4" type="number" step="0.1" value={s.min_weight} onChange={setRateSlab(i, "min_weight")} required data-testid={`portal-slab-${i}-min`} />
                    <Input className="col-span-4" type="number" step="0.1" value={s.max_weight} onChange={setRateSlab(i, "max_weight")} required data-testid={`portal-slab-${i}-max`} />
                    <Input className="col-span-3" type="number" step="1" value={s.price} onChange={setRateSlab(i, "price")} required data-testid={`portal-slab-${i}-price`} />
                    <button type="button" className="col-span-1 text-rose-600 disabled:opacity-30" disabled={rateForm.slabs.length === 1} onClick={() => removeRateSlab(i)} data-testid={`portal-remove-slab-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Per Extra kg (beyond top slab)</Label>
              <Input type="number" step="1" value={rateForm.per_kg_extra} onChange={(e) => setRateForm((f) => ({ ...f, per_kg_extra: e.target.value }))} placeholder="0" data-testid="portal-rate-perkg-input" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={rateSaving} data-testid="save-portal-rate-button">
                {rateSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save Rates
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrdersPanel() {
  const { source } = useSource();
  const [orders, setOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [portalFilter, setPortalFilter] = useState("ALL");
  const [portals, setPortals] = useState([]);
  const [courierAssignFor, setCourierAssignFor] = useState(null);
  const [courierAssignId, setCourierAssignId] = useState("");
  const [usersList, setUsersList] = useState([]);
  const [userAssignFor, setUserAssignFor] = useState(null);
  const [userAssignId, setUserAssignId] = useState("");
  const [saving, setSaving] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [courierFor, setCourierFor] = useState(null);
  const [courierForm, setCourierForm] = useState({ domestic_courier: "", pickup_tracking_number: "" });
  const [selectedIds, setSelectedIds] = useState([]);
  const [eventFor, setEventFor] = useState(null);
  const [eventForm, setEventForm] = useState({ title: "", note: "", date: "", event_type: "info" });
  const [statusFor, setStatusFor] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", note: "", location: "", date: "" });
  const [detail, setDetail] = useState(null);
  const [docType, setDocType] = useState(null);
  const [estFor, setEstFor] = useState(null);
  const [estForm, setEstForm] = useState({ est_delivery_date: "" });
  const [deliverFor, setDeliverFor] = useState(null);
  const [deliverForm, setDeliverForm] = useState({ location: "", received_by: "", date: "" });
  const openDeliver = (o) => { setDeliverFor(o); setDeliverForm({ location: o.bulk ? "" : `${o.receiver?.city ? o.receiver.city + ", " : ""}${o.destination_country || ""}`, received_by: "", date: new Date().toISOString().slice(0, 10) }); };
  const doDeliver = async () => {
    setSaving(true);
    try {
      if (deliverFor.bulk) {
        const { data } = await api.post("/orders/deliver/bulk", { order_ids: selectedIds, ...deliverForm });
        toast.success(`${data.updated} shipment(s) marked delivered${data.skipped ? `, ${data.skipped} skipped` : ""}`);
        setSelectedIds([]);
      } else {
        await api.post(`/orders/${deliverFor.id}/deliver`, deliverForm);
        toast.success("Marked delivered — POD generated");
      }
      celebrate("delivered"); setDeliverFor(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (portalFilter !== "ALL") params.portal = portalFilter;
      if (search) params.search = search;
      const [o, p] = await Promise.all([api.get("/orders", { params }), api.get("/portals")]);
      setOrders(o.data);
      setPortals(p.data);
      api.get("/couriers").then((r) => setCouriers(r.data)).catch(() => {});
      api.get("/users").then((r) => setUsersList(r.data.filter((u) => u.role === "user"))).catch(() => {});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, portalFilter]);

  const openCourier = (o) => { setCourierFor(o); setCourierForm({ domestic_courier: o.domestic_courier || "", pickup_tracking_number: o.pickup_tracking_number || "" }); };
  const openBulkCourier = () => { setCourierFor({ bulk: true }); setCourierForm({ domestic_courier: "", pickup_tracking_number: "" }); };
  const doCourier = async () => {
    if (!courierForm.domestic_courier) return;
    setSaving(true);
    try {
      if (courierFor.bulk) {
        const { data } = await api.post("/orders/courier/bulk", { order_ids: selectedIds, ...courierForm });
        toast.success(`Courier set on ${data.updated} shipment(s)`);
        setSelectedIds([]);
      } else {
        await api.patch(`/orders/${courierFor.id}/courier`, courierForm);
        toast.success("Domestic courier saved");
      }
      setCourierFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openCourierAssign = (o) => { setCourierAssignFor(o); setCourierAssignId(o.assigned_courier_id || ""); };
  const openBulkCourierAssign = () => { setCourierAssignFor({ bulk: true }); setCourierAssignId(""); };
  const doAssignCourier = async () => {
    if (!courierAssignId) return;
    setSaving(true);
    try {
      if (courierAssignFor.bulk) {
        const { data } = await api.post("/orders/assign-courier/bulk", { order_ids: selectedIds, courier_id: courierAssignId });
        toast.success(`Pickup assigned on ${data.updated} shipment(s)`);
        setSelectedIds([]);
      } else {
        await api.patch(`/orders/${courierAssignFor.id}/assign-courier`, { courier_id: courierAssignId });
        toast.success("Pickup courier assigned");
      }
      setCourierAssignFor(null);
      setCourierAssignId("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openUserAssign = (o) => { setUserAssignFor(o); setUserAssignId(o.user_id || ""); };
  const doAssignUser = async () => {
    if (!userAssignId) return;
    setSaving(true);
    try {
      await api.patch(`/orders/${userAssignFor.id}/assign-user`, { user_id: userAssignId });
      toast.success("Shipment assigned to customer");
      setUserAssignFor(null);
      setUserAssignId("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openStatus = (o) => { setStatusFor(o); setStatusForm({ status: "", note: "", location: "", date: "" }); };
  const openBulkStatus = () => { setStatusFor({ bulk: true }); setStatusForm({ status: "", note: "", location: "", date: "" }); };
  const openEst = (o) => { setEstFor(o); setEstForm({ est_delivery_date: o.est_delivery_date || "" }); };
  const openBulkEst = () => { setEstFor({ bulk: true }); setEstForm({ est_delivery_date: "" }); };
  const doEst = async () => {
    if (!estForm.est_delivery_date) return;
    setSaving(true);
    try {
      if (estFor.bulk) {
        const { data } = await api.post("/orders/est-delivery/bulk", { order_ids: selectedIds, est_delivery_date: estForm.est_delivery_date });
        toast.success(`ETA set on ${data.updated} shipment(s)`);
        setSelectedIds([]);
      } else {
        await api.patch(`/orders/${estFor.id}/est-delivery`, estForm);
        toast.success("Estimated delivery saved");
      }
      setEstFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const doStatus = async () => {
    if (!statusForm.status) return;
    setSaving(true);
    try {
      const payload = { status: statusForm.status, note: statusForm.note, location: statusForm.location, date: statusForm.date };
      if (statusFor.bulk) {
        const { data } = await api.post("/orders/status/bulk", { order_ids: selectedIds, ...payload });
        toast.success(`Updated ${data.updated} shipment(s)`);
        setSelectedIds([]);
      } else {
        await api.patch(`/orders/${statusFor.id}/status`, payload);
        toast.success("Status updated");
      }
      setStatusFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const openEvent = (o) => { setEventFor(o); setEventForm({ title: "", note: "", date: "", event_type: o.on_hold ? "resume" : "info" }); };
  const deleteOrder = async (o) => {
      if (!window.confirm("Delete shipment " + o.tracking_number + "? This cannot be undone.")) return;
      try {
        await api.delete("/orders/" + o.id);
        toast.success("Shipment deleted");
        load();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    };

    const doEvent = async () => {
    if (!eventForm.title) return;
    setSaving(true);
    try {
      await api.post(`/orders/${eventFor.id}/event`, eventForm);
      toast.success("Update added to tracking");
      setEventFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const visible = orders.filter((o) => !source || source === "ALL" || o.source_portal === source);

  return (
    <div className="space-y-4">
      <div className="panel flex flex-wrap items-center gap-2 p-3" data-testid="admin-orders-toolbar">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-1 gap-2 min-w-[240px]">
          <div className="relative flex-1 glow-ring">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search tracking / customer…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-order-search" />
          </div>
          <Button type="submit" variant="outline">Search</Button>
        </form>
        <Select value={statusFilter} onValueChange={(v) => { triggerHaptic("tap"); setStatusFilter(v); }}>
          <SelectTrigger className="h-10 w-[170px] rounded-[10px]" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={portalFilter} onValueChange={(v) => { triggerHaptic("tap"); setPortalFilter(v); }}>
          <SelectTrigger className="h-10 w-[170px] rounded-[10px]" data-testid="admin-portal-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Portals</SelectItem>
            {portals.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex flex-wrap gap-2">
          
          <Button className="shine-hover" onClick={() => setBookingOpen(true)} data-testid="admin-new-order-button"><Plus className="h-4 w-4" /> New Booking</Button>
        </div>
        <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} onCreated={load} />
      </div>

      {selectedIds.length > 0 && (
        <div className="glass sticky top-16 z-20 flex flex-wrap items-center gap-2 rounded-2xl border border-primary/25 px-4 py-2.5 shadow-[0_16px_40px_-20px_rgba(33,79,216,.5)] fade-up" data-testid="admin-bulk-toolbar">
          <span className="flex items-center gap-2 text-sm font-semibold"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-white">{selectedIds.length}</span> selected</span>
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Button size="sm" variant="outline" onClick={openBulkStatus} data-testid="bulk-status-button"><MapPin className="h-4 w-4" /> Set Status</Button>
            <Button size="sm" variant="outline" onClick={openBulkCourier} data-testid="bulk-courier-button"><Truck className="h-4 w-4" /> Set Courier</Button>
            <Button size="sm" variant="outline" onClick={openBulkCourierAssign} data-testid="bulk-assign-courier-button"><UserCog className="h-4 w-4" /> Assign Pickup</Button>
            <Button size="sm" variant="outline" onClick={openBulkEst} data-testid="bulk-eta-button"><CalendarClock className="h-4 w-4" /> Set ETA</Button>
            <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => openDeliver({ bulk: true })} data-testid="bulk-deliver-button"><CheckCircle2 className="h-4 w-4" /> Mark Delivered</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} data-testid="bulk-clear-button"><X className="h-4 w-4" /> Clear</Button>
          </div>
        </div>
      )}

      <div className="panel overflow-hidden md:overflow-x-auto" data-testid="admin-orders-table">
        <div className="hidden md:grid min-w-[900px] grid-cols-12 gap-4 border-b border-border bg-slate-50/70 px-5 py-2.5 text-[11px] font-mono-label font-semibold uppercase tracking-widest text-muted-foreground">
          <div className="col-span-3">Shipment</div>
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Courier</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <ListSkeleton rows={6} />
        ) : visible.length === 0 ? (
          <EmptyState icon={Package} title="No orders found" hint="Adjust your search or filters, or create a new booking." testId="admin-orders-empty" className="rounded-none border-0 shadow-none" />
        ) : visible.map((o) => (
          <div key={o.id} className="group grid grid-cols-1 gap-3 border-b border-border/60 px-4 py-4 md:min-w-[900px] md:grid-cols-12 md:items-center md:gap-4 md:px-5 md:py-3.5 transition-colors last:border-0 hover:bg-primary/[.025]" data-testid={`admin-order-${o.tracking_number}`}>
            <div className="md:col-span-3 flex min-w-0 items-start gap-3">
              <input type="checkbox" checked={selectedIds.includes(o.id)} onChange={() => toggleSelect(o.id)} className="mt-1 h-4 w-4 shrink-0 rounded border-border" data-testid={`select-${o.tracking_number}`} />
              <div className="min-w-0">
                <TrackingPill tn={o.tracking_number} />
                <button type="button" className="mt-1 block max-w-full truncate text-left text-sm font-semibold text-foreground transition-colors hover:text-primary" onClick={() => setDetail(o)} data-testid={`admin-open-${o.tracking_number}`}>{o.recipient_name}</button>
                <div className="truncate text-xs text-muted-foreground">{o.delivery_address}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <PortalBadge portal={o.source_portal} testId={`admin-portal-${o.tracking_number}`} />
                  <Route from={o.origin_country} to={o.destination_country} />
                  {o.quoted_price ? <span className="text-[11px] font-semibold text-foreground">₹{o.quoted_price.toLocaleString("en-IN")}</span> : null}
                </div>
              </div>
            </div>
            <div className="md:col-span-3 min-w-0 text-sm">
              <div className="truncate font-medium">{o.user_name}</div>
              <div className="truncate text-xs text-muted-foreground">{o.user_email}</div>
              {(o.is_guest || !o.user_id) && (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700" data-testid={`guest-badge-${o.tracking_number}`}>Guest</span>
                  <button type="button" className="text-[11px] font-semibold text-primary hover:underline" onClick={() => openUserAssign(o)} data-testid={`assign-user-btn-${o.tracking_number}`}>Assign Customer</button>
                </div>
              )}
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <StatusBadge status={o.status} />
              {o.on_hold && <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-700" data-testid={`hold-badge-${o.tracking_number}`}><AlertTriangle className="h-3 w-3" /> Hold</span>}
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground"><CalendarClock className="h-3 w-3" /> {o.est_delivery_date ? `ETA ${o.est_delivery_date}` : "No ETA"}</div>
            </div>
            <div className="md:col-span-2 min-w-0 space-y-1 text-xs">
              {o.assigned_courier_name ? (
                <button type="button" onClick={() => openCourierAssign(o)} className="flex max-w-full items-center gap-1 text-orange-600 hover:underline" data-testid={`courier-assigned-${o.tracking_number}`}><UserCog className="h-3 w-3 shrink-0" /> <span className="truncate">{o.assigned_courier_name}</span></button>
              ) : (
                <span className="flex items-center gap-1 text-muted-foreground"><UserCog className="h-3 w-3" /> Pickup unassigned</span>
              )}
              {o.domestic_courier ? (
                <button type="button" onClick={() => openCourier(o)} className="flex max-w-full items-center gap-1 font-medium text-foreground hover:underline" data-testid={`courier-info-${o.tracking_number}`}><Truck className="h-3 w-3 shrink-0 text-primary" /> <span className="truncate">{o.domestic_courier}{o.pickup_tracking_number ? ` · ${o.pickup_tracking_number}` : ""}</span></button>
              ) : (
                <span className="flex items-center gap-1 text-muted-foreground"><Truck className="h-3 w-3" /> No domestic courier</span>
              )}
            </div>
            <div className="md:col-span-2 flex flex-wrap items-center justify-start gap-1.5 md:justify-end">
              {o.status !== "Delivered" && <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => openDeliver(o)} data-testid={`deliver-btn-${o.tracking_number}`}><CheckCircle2 className="h-4 w-4" /> Delivered</Button>}
              <Button size="sm" variant="outline" onClick={() => openStatus(o)} data-testid={`status-btn-${o.tracking_number}`}>Set Status</Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`row-menu-${o.tracking_number}`} aria-label="More actions"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl p-1.5">
                  <DropdownMenuItem className="gap-2 rounded-lg" onClick={() => setDetail(o)} data-testid={`detail-btn-${o.tracking_number}`}><FileText className="h-4 w-4 text-primary" /> Details & documents</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2 rounded-lg" onClick={() => openEst(o)} data-testid={`eta-btn-${o.tracking_number}`}><CalendarClock className="h-4 w-4 text-primary" /> {o.est_delivery_date ? "Change ETA" : "Set ETA"}</DropdownMenuItem>
                  <DropdownMenuItem className="gap-2 rounded-lg" onClick={() => openCourierAssign(o)} data-testid={`assign-courier-btn-${o.tracking_number}`}><UserCog className="h-4 w-4 text-primary" /> Assign pickup courier</DropdownMenuItem>
                  <DropdownMenuItem className="gap-2 rounded-lg" onClick={() => openCourier(o)} data-testid={`courier-btn-${o.tracking_number}`}><Truck className="h-4 w-4 text-primary" /> Domestic courier</DropdownMenuItem>
                  <DropdownMenuItem className="gap-2 rounded-lg" onClick={() => openEvent(o)} data-testid={`event-btn-${o.tracking_number}`}><AlertTriangle className="h-4 w-4 text-primary" /> Add tracking update</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2 rounded-lg text-rose-600 focus:text-rose-600" onClick={() => deleteOrder(o)} data-testid={"delete-btn-" + o.tracking_number}><Trash2 className="h-4 w-4" /> Delete shipment</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!courierAssignFor} onOpenChange={(v) => { if (!v) { setCourierAssignFor(null); setCourierAssignId(""); } }}>
        <DialogContent data-testid="assign-courier-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Assign Pickup Courier</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{courierAssignFor?.bulk ? `Apply to ${selectedIds.length} selected shipment(s)` : `Order ${courierAssignFor?.tracking_number} · first-mile pickup`}</p>
          <Select value={courierAssignId} onValueChange={setCourierAssignId}>
            <SelectTrigger data-testid="assign-courier-select"><SelectValue placeholder="Select courier" /></SelectTrigger>
            <SelectContent>
              {couriers.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No courier accounts yet</div> : couriers.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`courier-option-${c.id}`}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button onClick={doAssignCourier} disabled={saving || !courierAssignId} data-testid="confirm-assign-courier-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Assign Pickup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!userAssignFor} onOpenChange={(v) => { if (!v) { setUserAssignFor(null); setUserAssignId(""); } }}>
        <DialogContent data-testid="assign-user-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Assign to Customer</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Link guest shipment {userAssignFor?.tracking_number} to a registered customer account.</p>
          <Select value={userAssignId} onValueChange={setUserAssignId}>
            <SelectTrigger data-testid="assign-user-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
            <SelectContent>
              {usersList.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No customer accounts</div> : usersList.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`user-option-${u.id}`}>{u.name} · {u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button onClick={doAssignUser} disabled={saving || !userAssignId} data-testid="confirm-assign-user-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Assign Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!courierFor} onOpenChange={(v) => { if (!v) setCourierFor(null); }}>
        <DialogContent data-testid="courier-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Domestic Courier</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{courierFor?.bulk ? `Apply to ${selectedIds.length} selected shipment(s)` : `Order ${courierFor?.tracking_number} · first-mile pickup (${courierFor?.booking_city || "origin"} → New Delhi)`}</p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Courier Name</Label>
              <Input value={courierForm.domestic_courier} onChange={(e) => setCourierForm((f) => ({ ...f, domestic_courier: e.target.value }))} placeholder="e.g. Delhivery, BlueDart, DTDC" data-testid="courier-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Pickup Tracking Number <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={courierForm.pickup_tracking_number} onChange={(e) => setCourierForm((f) => ({ ...f, pickup_tracking_number: e.target.value }))} placeholder="e.g. DL123456789" data-testid="courier-tracking-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doCourier} disabled={saving || !courierForm.domestic_courier} data-testid="confirm-courier-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save Courier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!eventFor} onOpenChange={(v) => { if (!v) setEventFor(null); }}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Add Tracking Update</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Order {eventFor?.tracking_number} — add a custom event (does not change the main stage).</p>
          <div className="flex flex-wrap gap-1.5">
            {["Shipment Hold", "Flight Rescheduled", "Delay", "Customs Query", "Damaged in Transit", "Address Issue", "Resumed"].map((p) => (
              <button key={p} type="button" onClick={() => setEventForm((f) => ({ ...f, title: p, event_type: p === "Resumed" ? "resume" : /hold/i.test(p) ? "hold" : f.event_type === "resume" ? "info" : f.event_type }))} className="rounded-full border border-border px-2.5 py-1 text-xs hover:bg-secondary" data-testid={`event-preset-${p.replace(/ /g, "-").toLowerCase()}`}>{p}</button>
            ))}
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input value={eventForm.title} onChange={(e) => setEventForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Flight rescheduled to 22 Jun" data-testid="event-title-input" /></div>
            <div className="space-y-1.5"><Label>Details <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={eventForm.note} onChange={(e) => setEventForm((f) => ({ ...f, note: e.target.value }))} placeholder="Reason / extra info shown to customer" data-testid="event-note-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={eventForm.date} onChange={(e) => setEventForm((f) => ({ ...f, date: e.target.value }))} data-testid="event-date-input" /></div>
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={eventForm.event_type} onValueChange={(v) => setEventForm((f) => ({ ...f, event_type: v }))}>
                  <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="hold">Hold / Exception</SelectItem>
                    <SelectItem value="resume">Resume (clear hold)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doEvent} disabled={saving || !eventForm.title} data-testid="confirm-event-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Add Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!statusFor} onOpenChange={(v) => { if (!v) setStatusFor(null); }}>
        <DialogContent data-testid="status-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Update Status</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{statusFor?.bulk ? `Apply to ${selectedIds.length} selected shipment(s)` : `Order ${statusFor?.tracking_number}`}</p>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={statusForm.status} onValueChange={(v) => setStatusForm((f) => ({ ...f, status: v }))}><SelectTrigger data-testid="status-input"><SelectValue placeholder="Choose stage" /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Location <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={statusForm.location} onChange={(e) => setStatusForm((f) => ({ ...f, location: e.target.value }))} placeholder="e.g. New Delhi" data-testid="status-location-input" /></div>
              <div className="space-y-1.5"><Label>Date <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="date" value={statusForm.date} onChange={(e) => setStatusForm((f) => ({ ...f, date: e.target.value }))} data-testid="status-date-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={statusForm.note} onChange={(e) => setStatusForm((f) => ({ ...f, note: e.target.value }))} data-testid="status-note-input" /></div>
          </div>
          <DialogFooter>
            <Button onClick={doStatus} disabled={saving || !statusForm.status || (statusFor?.bulk && selectedIds.length === 0)} data-testid="confirm-status-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) { setDetail(null); setDocType(null); } }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="admin-detail-dialog">
          {detail && !docType && (
            <>
              <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{detail.tracking_number}</DialogTitle></DialogHeader>
              <div className="flex items-center justify-between">
                <div className="text-sm"><div className="font-medium">{detail.recipient_name}</div><div className="text-xs text-muted-foreground">{detail.delivery_address}</div></div>
                <StatusBadge status={detail.status} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-border p-3"><div className="text-[10px] font-mono-label uppercase text-muted-foreground">Sender</div><div className="font-medium">{detail.sender?.company_name || detail.sender?.full_name}</div><div className="text-xs text-muted-foreground">{detail.pickup_address}</div></div>
                <div className="rounded-lg border border-border p-3"><div className="text-[10px] font-mono-label uppercase text-muted-foreground">Receiver</div><div className="font-medium">{detail.recipient_name}</div><div className="text-xs text-muted-foreground">{detail.delivery_address}</div></div>
              </div>
              <div className="rounded-lg border border-border p-3 text-sm space-y-1">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Booking Type</span><div className="font-semibold">{detail.booking_type}</div></div>
                  <div><span className="text-muted-foreground">Source</span><div className="font-semibold">{detail.source_portal}</div></div>
                  <div><span className="text-muted-foreground">AWB No.</span><div className="font-semibold">{detail.awb_number || "—"}</div></div>
                  <div><span className="text-muted-foreground">Invoice No.</span><div className="font-semibold">{detail.invoice_number || "—"}</div></div>
                  <div><span className="text-muted-foreground">Domestic Courier</span><div className="font-semibold">{detail.domestic_courier || "—"} {detail.pickup_tracking_number ? `(${detail.pickup_tracking_number})` : ""}</div></div>
                  <div><span className="text-muted-foreground">Declared Value</span><div className="font-semibold">₹{Number(detail.declared_value || 0).toLocaleString("en-IN")}</div></div>
                  <div><span className="text-muted-foreground">Est. Delivery</span><div className="font-semibold">{detail.est_delivery_date || "—"}</div></div>
                </div>
                <div className="pt-1"><span className="text-muted-foreground text-xs">Boxes ({detail.box_count} · {detail.weight} kg)</span>
                  {(detail.boxes || []).map((b, i) => <div key={i} className="text-xs text-muted-foreground">Box {i + 1}: {b.length}×{b.width}×{b.height} cm · {b.weight} kg · {b.item_content}</div>)}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setDocType("invoice")} data-testid="admin-invoice-button">Invoice</Button>
                <Button size="sm" variant="outline" onClick={() => setDocType("proforma")} data-testid="admin-proforma-button">Proforma</Button>
                <Button size="sm" variant="outline" onClick={() => setDocType("awb")} data-testid="admin-awb-button">AWB Label</Button>
                {detail.status === "Delivered" && <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300" onClick={() => setDocType("pod")} data-testid="admin-pod-button">Proof of Delivery</Button>}
              </div>
              <Milestones order={detail} testId="admin-milestones" />
              <Timeline timeline={detail.timeline} status={detail.status} order={detail} />
            </>
          )}
          {detail && docType && (
            <>
              <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl no-print flex items-center gap-3">{docType === "invoice" ? "Invoice" : docType === "proforma" ? "Proforma Invoice" : docType === "pod" ? "Proof of Delivery" : "AWB Label"}<Button size="sm" variant="ghost" onClick={() => setDocType(null)} className="no-print">← Back</Button></DialogTitle></DialogHeader>
              {docType === "invoice" ? <InvoiceView order={detail} /> : docType === "proforma" ? <ProformaInvoice order={detail} /> : docType === "pod" ? <PodDocument order={detail} /> : <AwbLabel order={detail} />}
              <DialogFooter className="no-print"><Button onClick={() => window.print()} data-testid="admin-print-button">Download / Print</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!estFor} onOpenChange={(v) => { if (!v) setEstFor(null); }}>
        <DialogContent data-testid="eta-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Estimated Delivery Date</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{estFor?.bulk ? `Apply to ${selectedIds.length} selected shipment(s)` : `Order ${estFor?.tracking_number}`}</p>
          <div className="space-y-1.5"><Label>Estimated Delivery Date</Label><Input type="date" value={estForm.est_delivery_date} onChange={(e) => setEstForm({ est_delivery_date: e.target.value })} data-testid="eta-date-input" /></div>
          <DialogFooter>
            <Button onClick={doEst} disabled={saving || !estForm.est_delivery_date || (estFor?.bulk && selectedIds.length === 0)} data-testid="confirm-eta-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save ETA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deliverFor} onOpenChange={(v) => { if (!v) setDeliverFor(null); }}>
        <DialogContent data-testid="admin-deliver-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">Mark Delivered</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{deliverFor?.bulk ? `Apply to ${selectedIds.length} selected shipment(s) — cancelled or already delivered ones are skipped.` : `${deliverFor?.tracking_number} · a Proof of Delivery is generated automatically.`}</p>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Delivery Location <span className="font-normal text-muted-foreground">(optional)</span></Label><Input value={deliverForm.location} onChange={(e) => setDeliverForm((f) => ({ ...f, location: e.target.value }))} placeholder="City, country" data-testid="admin-deliver-location" /></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Received By <span className="font-normal text-muted-foreground">(optional)</span></Label><Input value={deliverForm.received_by} onChange={(e) => setDeliverForm((f) => ({ ...f, received_by: e.target.value }))} data-testid="admin-deliver-receivedby" /></div>
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={deliverForm.date} onChange={(e) => setDeliverForm((f) => ({ ...f, date: e.target.value }))} data-testid="admin-deliver-date" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="success" onClick={doDeliver} disabled={saving || (deliverFor?.bulk && selectedIds.length === 0)} data-testid="admin-confirm-deliver">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm Delivered
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WarehousePanel() {
  const { source } = useSource();
  const [orders, setOrders] = useState([]);
  const [portals, setPortals] = useState([]);
  const [country, setCountry] = useState("ALL");
  const [rdate, setRdate] = useState(new Date().toISOString().slice(0, 10));
  const [code, setCode] = useState("");
  const [scanVal, setScanVal] = useState("");
  const [found, setFound] = useState(null);
  const [docFor, setDocFor] = useState(null);
  const [flash, setFlash] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const readerRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/orders");
    setOrders(data);
  };
  useEffect(() => { load(); api.get("/portals").then((r) => setPortals(r.data)).catch(() => {}); }, []);

  const countries = ["ALL", ...Array.from(new Set(portals.map((p) => p.destination_country)))];
  const byCountry = (o) => (country === "ALL" || o.destination_country === country) && (!source || source === "ALL" || o.source_portal === source);
  const incoming = orders.filter((o) => byCountry(o) && !o.warehouse_received);
  const received = orders.filter(byCountry).flatMap((o) => (o.boxes || []).map((b, i) => ({ o, b, i })).filter((x) => x.b.received && (!rdate || x.b.received_date === rdate)));

  const scanCode = async (override) => {
    const c = (override ?? scanVal).trim();
    if (!c) { toast.error("Scan an AWB / box label"); return; }
    setScanning(true);
    try {
      const { data } = await api.post("/warehouse/scan-code", { code: c });
      if (data.already) toast.info(`All ${data.total} box(es) already received for ${data.order.tracking_number}`);
      else if (data.complete) { toast.success(`✓ All ${data.total} box(es) received — ${data.order.tracking_number} at New Delhi hub`); triggerHaptic("success"); }
      else { toast.success(`Box scanned · ${data.received}/${data.total} received — scan ${data.total - data.received} more box(es)`); triggerHaptic("scan_received"); }
      setFlash(true); setTimeout(() => setFlash(false), 900);
      setScanVal(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setScanning(false); }
  };
  useEffect(() => {
    if (!cameraOpen) {
      if (readerRef.current) { try { readerRef.current.reset(); } catch (e) {} readerRef.current = null; }
      return;
    }
    let active = true;
    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        readerRef.current = reader;
        await reader.decodeFromConstraints(
          { video: { facingMode: "environment" } },
          videoRef.current,
          (result) => {
            if (!active || !result) return;
            const text = result.getText();
            setScanVal(text);
            setCameraOpen(false);
            scanCode(text);
          }
        );
      } catch (e) {
        toast.error("Camera error: " + (e?.message || "could not start camera"));
        setCameraOpen(false);
      }
    })();
    return () => {
      active = false;
      if (readerRef.current) { try { readerRef.current.reset(); } catch (e) {} readerRef.current = null; }
    };
  }, [cameraOpen]);

  const lookup = () => { const o = orders.find((x) => x.tracking_number === code.trim() || x.awb_number === code.trim()); setFound(o || null); if (!o) toast.error("Not found"); else triggerHaptic("success"); };

  const CountrySel = () => (
    <Select value={country} onValueChange={(v) => { triggerHaptic("tap"); setCountry(v); }}>
      <SelectTrigger className="h-10 w-[200px] rounded-[10px]" data-testid="wh-country-select"><SelectValue /></SelectTrigger>
      <SelectContent>{countries.map((c) => <SelectItem key={c} value={c}>{c === "ALL" ? "All Countries" : c}</SelectItem>)}</SelectContent>
    </Select>
  );

  const BoxChips = ({ o }) => {
    const bx = o.boxes || [];
    return (
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {bx.map((b, i) => (
          <span key={i} className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium transition-all ${b.received ? "border-emerald-300 bg-emerald-50 text-emerald-700 pop-in" : "border-dashed border-border text-muted-foreground"}`} data-testid={`wh-box-status-${o.tracking_number}-${i}`}>
            {b.received ? <CheckCircle2 className="h-3 w-3" /> : <span className="h-3 w-3 rounded-full border border-current" />}
            Box {i + 1}{b.received ? "" : " pending"}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="warehouse-panel">
      <Tabs defaultValue="incoming">
        <TabsList className="h-auto w-full flex-wrap justify-start sm:w-auto">
          <TabsTrigger value="incoming" data-testid="wh-tab-incoming"><ScanLine className="h-4 w-4" /> Incoming <span className="ml-1 rounded-full bg-primary/10 px-1.5 text-[10px] font-bold text-primary">{incoming.length}</span></TabsTrigger>
          <TabsTrigger value="received" data-testid="wh-tab-received"><CheckCircle2 className="h-4 w-4" /> Received</TabsTrigger>
          <TabsTrigger value="lookup" data-testid="wh-tab-lookup"><Search className="h-4 w-4" /> Lookup</TabsTrigger>
        </TabsList>

        <TabsContent value="incoming" className="mt-4 space-y-4">
          <div className={`panel relative overflow-hidden p-5 transition-all ${flash ? "scan-flash border-emerald-400" : ""}`} data-testid="wh-scan-box">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
            <div className="relative flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 font-display text-lg font-bold tracking-tight"><span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20"><ScanLine className="h-4 w-4" /></span> Scan to receive</div>
                <p className="mt-1 text-xs text-muted-foreground">Scanning is compulsory. <b className="text-foreground">Each scan receives one box</b> — a 3-box shipment needs 3 scans.</p>
              </div>
              <CountrySel />
            </div>
            <div className="relative mt-4 flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1 glow-ring">
                <ScanLine className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-primary" />
                <Input autoFocus className="h-12 pl-11 text-base font-mono-label" placeholder="Scan / enter AWB or box label…" value={scanVal} onChange={(e) => setScanVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") scanCode(); }} data-testid="wh-scan-input" />
              </div>
              <Button size="lg" className="h-12 shine-hover" onClick={scanCode} disabled={scanning} haptic="scan_received" data-testid="wh-scan-submit">{scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />} Scan</Button>
        <Button variant="outline" onClick={() => setCameraOpen((v) => !v)} data-testid="wh-scan-camera-toggle">{cameraOpen ? <X className="h-4 w-4" /> : <Camera className="h-4 w-4" />} {cameraOpen ? "Close Camera" : "Scan with Camera"}</Button>
            </div>
          </div>
          {cameraOpen ? (
          <div className="mt-4 rounded-xl border border-border/60 bg-muted/30 p-3">
            <video ref={videoRef} className="w-full max-w-sm mx-auto rounded-lg" muted playsInline />
            <p className="mt-2 text-xs text-muted-foreground text-center">Point the camera at the AWB / box barcode or QR code.</p>
          </div>
        ) : null}
        {incoming.length === 0 ? <EmptyState icon={CheckCircle2} title="No incoming shipments" hint="Everything for this source and country has been received." testId="wh-incoming-empty" /> : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {incoming.map((o, idx) => {
                const bx = o.boxes || [];
                const tot = bx.length || 1;
                const rec = bx.filter((b) => b.received).length;
                const pct = Math.round((rec / tot) * 100);
                return (
                  <div key={o.id} className="panel hover-lift fade-up p-4" style={{ "--i": Math.min(idx, 8) }} data-testid={`wh-incoming-${o.tracking_number}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0"><TrackingPill tn={o.tracking_number} /><div className="mt-1 truncate text-xs text-muted-foreground">{o.recipient_name} · {o.source_portal} → {o.destination_country}</div></div>
                      <StatusBadge status={o.status} />
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs font-semibold" data-testid={`wh-progress-${o.tracking_number}`}>
                      <span className="text-primary">{tot} box{tot === 1 ? "" : "es"} · {rec}/{tot} received</span>
                      <span className="text-muted-foreground">{rec < tot ? `scan ${tot - rec} more` : "✓ all received"}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-500 transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} /></div>
                    <BoxChips o={o} />
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="received" className="mt-4 space-y-3">
          <div className="panel flex flex-wrap items-center gap-2 p-3"><CountrySel /><Input type="date" className="w-[180px]" value={rdate} onChange={(e) => setRdate(e.target.value)} data-testid="wh-received-date" /><span className="ml-auto text-xs text-muted-foreground" data-testid="wh-received-count">{received.length} box{received.length === 1 ? "" : "es"}</span></div>
          {received.length === 0 ? <EmptyState icon={Package} title="No boxes scanned" hint="Nothing was received for this date and country." testId="wh-received-empty" /> : (
            <div className="panel divide-y divide-border/60 overflow-hidden" data-testid="wh-received-list">
              {received.map(({ o, b, i }) => (
                <div key={`${o.id}-${i}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-primary/[.025]">
                  <div className="flex items-center gap-2.5"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /></span><TrackingPill tn={o.tracking_number} /><span className="text-xs font-semibold text-muted-foreground">Box {i + 1}</span></div>
                  <span className="truncate text-xs text-muted-foreground">{b.item_content} · {b.weight}kg · {o.destination_country}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="lookup" className="mt-4 space-y-4">
          <div className="panel p-5">
            <div className="flex items-center gap-2 font-display text-lg font-bold tracking-tight"><span className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-600 ring-1 ring-cyan-500/20"><Search className="h-4 w-4" /></span> Shipment lookup</div>
            <p className="mt-1 text-xs text-muted-foreground">Scan or enter an AWB to see box contents, receipt progress and print documents.</p>
            <div className="mt-4 flex gap-2">
              <div className="relative flex-1 glow-ring">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input autoFocus className="h-12 pl-10 font-mono-label" placeholder="Scan/enter AWB or tracking number" value={code} onChange={(e) => { setCode(e.target.value); setFound(null); }} onKeyDown={(e) => { if (e.key === "Enter") lookup(); }} data-testid="wh-lookup-input" />
              </div>
              <Button size="lg" className="h-12" onClick={lookup} disabled={!code.trim()} data-testid="wh-lookup-btn">Lookup</Button>
            </div>
          </div>
          {found && (
            <div className="panel fade-up space-y-3 p-5" data-testid="wh-lookup-result">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2"><TrackingPill tn={found.tracking_number} /><PortalBadge portal={found.source_portal} /></div>
                <StatusBadge status={found.status} />
              </div>
              <div className="text-sm"><span className="font-semibold">{found.recipient_name}</span> <span className="text-muted-foreground">· {found.source_portal} → {found.destination_country}</span></div>
              {(() => { const bx = found.boxes || []; const tot = bx.length || 1; const rec = bx.filter((b) => b.received).length; return (
                <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold ${rec === tot ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-primary/20 bg-primary/5 text-primary"}`} data-testid="wh-lookup-progress">{rec === tot ? <CheckCircle2 className="h-4 w-4" /> : <ScanLine className="h-4 w-4" />}{tot} box{tot === 1 ? "" : "es"} · {rec}/{tot} received{rec < tot ? ` — scan ${tot - rec} more box(es)` : " — ✓ all received"}</div>
              ); })()}
              <div className="grid gap-2 sm:grid-cols-2">
                {(found.boxes || []).map((b, i) => (
                  <div key={i} className={`rounded-xl border p-3 text-xs ${b.received ? "border-emerald-200 bg-emerald-50/50" : "border-border"}`}>
                    <div className="flex items-center justify-between font-semibold"><span>Box {i + 1} · {b.length}×{b.width}×{b.height}cm · {b.weight}kg</span>{b.received && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}</div>
                    {(b.items || []).map((it, j) => <div key={j} className="mt-0.5 text-muted-foreground">{it.name} × {it.quantity} · ₹{Number(it.value || 0).toLocaleString("en-IN")}</div>)}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 rounded-xl border border-dashed border-border bg-slate-50/50 p-3">
                <span className="mr-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><FileText className="h-3.5 w-3.5" /> Documents</span>
                <Button size="sm" variant="outline" onClick={() => setDocFor({ o: found, t: "awb" })} data-testid="wh-print-awb">AWB Label</Button>
                <Button size="sm" variant="outline" onClick={() => setDocFor({ o: found, t: "proforma" })} data-testid="wh-print-proforma">Proforma</Button>
                {found.status === "Delivered" && <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" onClick={() => setDocFor({ o: found, t: "pod" })} data-testid="wh-print-pod">Proof of Delivery</Button>}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!docFor} onOpenChange={(v) => { if (!v) setDocFor(null); }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="wh-doc-dialog">
          <DialogHeader className="no-print"><DialogTitle className="text-2xl">{docFor?.t === "awb" ? "AWB Label" : docFor?.t === "pod" ? "Proof of Delivery" : "Proforma Invoice"}</DialogTitle></DialogHeader>
          {docFor && (docFor.t === "awb" ? <AwbLabel order={docFor.o} /> : docFor.t === "pod" ? <PodDocument order={docFor.o} /> : <ProformaInvoice order={docFor.o} />)}
          <DialogFooter className="no-print"><Button onClick={() => window.print()} data-testid="wh-print-btn"><Download className="h-4 w-4" /> Download / Print</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RatesPanel() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const EMPTY = { destination_country: "", service_type: "DDP", per_kg_extra: "", slabs: [{ min_weight: "0", max_weight: "1", price: "" }] };
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/rates"); setRates(data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      destination_country: r.destination_country, service_type: r.service_type,
      per_kg_extra: String(r.per_kg_extra ?? ""),
      slabs: r.slabs.map((s) => ({ min_weight: String(s.min_weight), max_weight: String(s.max_weight), price: String(s.price) })),
    });
    setOpen(true);
  };

  const setSlab = (i, k) => (e) => setForm((f) => ({ ...f, slabs: f.slabs.map((s, idx) => (idx === i ? { ...s, [k]: e.target.value } : s)) }));
  const addSlab = () => setForm((f) => ({ ...f, slabs: [...f.slabs, { min_weight: "", max_weight: "", price: "" }] }));
  const removeSlab = (i) => setForm((f) => ({ ...f, slabs: f.slabs.length > 1 ? f.slabs.filter((_, idx) => idx !== i) : f.slabs }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const slabs = form.slabs.map((s) => ({ min_weight: parseFloat(s.min_weight) || 0, max_weight: parseFloat(s.max_weight) || 0, price: parseFloat(s.price) || 0 }));
      const per = parseFloat(form.per_kg_extra) || 0;
      if (editing) {
        await api.patch(`/rates/${editing.id}`, { slabs, per_kg_extra: per });
        toast.success("Rate card updated");
      } else {
        await api.post("/rates", { destination_country: form.destination_country, service_type: form.service_type, slabs, per_kg_extra: per });
        toast.success("Rate card added");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const del = async (r) => {
    try { await api.delete(`/rates/${r.id}`); toast.success("Rate deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="rates-panel">
      <div className="flex items-center justify-end">
        <Button onClick={openNew} className="shine-hover" data-testid="add-rate-button"><Plus className="h-4 w-4" /> Add Rate</Button>
      </div>

      {loading ? (
        <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rates.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground" data-testid="rates-empty">No rate cards yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rates.map((r) => (
            <div key={r.id} className="panel hover-lift p-5" data-testid={`rate-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Coins className="h-5 w-5" /></div>
                  <div>
                    <div className="font-display text-lg font-bold">{r.destination_country}</div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{r.service_type}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-rate-${r.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => del(r)} data-testid={`delete-rate-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-3 rounded-lg border border-border overflow-hidden">
                <div className="grid grid-cols-2 bg-secondary/50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <span>Weight (kg)</span><span className="text-right">Price</span>
                </div>
                {r.slabs.map((s, i) => (
                  <div key={i} className="grid grid-cols-2 px-3 py-1.5 text-sm border-t border-border/60">
                    <span>{s.min_weight}–{s.max_weight}</span>
                    <span className="text-right font-semibold">₹{s.price.toLocaleString("en-IN")}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-xs text-muted-foreground">+ ₹{(r.per_kg_extra || 0).toLocaleString("en-IN")} per extra kg beyond top slab</div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="rate-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{editing ? "Edit Rate Card" : "Add Rate Card"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="rate-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Destination Country</Label>
                <Select value={form.destination_country} onValueChange={(v) => setForm((f) => ({ ...f, destination_country: v }))} disabled={!!editing}>
                  <SelectTrigger data-testid="rate-country-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{RATE_COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Service Type</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))} disabled={!!editing}>
                  <SelectTrigger data-testid="rate-service-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{RATE_SERVICES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Weight Slabs</Label>
                <Button type="button" size="sm" variant="outline" onClick={addSlab} data-testid="add-slab-button"><Plus className="h-4 w-4 mr-1" /> Slab</Button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1">
                  <span className="col-span-4">Min kg</span><span className="col-span-4">Max kg</span><span className="col-span-3">Price ₹</span><span className="col-span-1" />
                </div>
                {form.slabs.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`slab-${i}`}>
                    <Input className="col-span-4" type="number" step="0.1" value={s.min_weight} onChange={setSlab(i, "min_weight")} required data-testid={`slab-${i}-min`} />
                    <Input className="col-span-4" type="number" step="0.1" value={s.max_weight} onChange={setSlab(i, "max_weight")} required data-testid={`slab-${i}-max`} />
                    <Input className="col-span-3" type="number" step="1" value={s.price} onChange={setSlab(i, "price")} required data-testid={`slab-${i}-price`} />
                    <button type="button" className="col-span-1 text-rose-600 disabled:opacity-30" disabled={form.slabs.length === 1} onClick={() => removeSlab(i)} data-testid={`remove-slab-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Per Extra kg (beyond top slab)</Label>
              <Input type="number" step="1" value={form.per_kg_extra} onChange={(e) => setForm((f) => ({ ...f, per_kg_extra: e.target.value }))} placeholder="0" data-testid="rate-perkg-input" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="save-rate-button">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing ? "Save Changes" : "Add Rate Card"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "courier", label: "Courier" },
  { value: "operations", label: "Operations" },
  { value: "admin", label: "Admin" },
];
const ROLE_COLOR = { admin: "text-primary", courier: "text-orange-600", operations: "text-cyan-600", user: "text-emerald-600" };

function UsersPanel() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [query, setQuery] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [creating, setCreating] = useState(false);

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "user" });
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [delUser, setDelUser] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createUser = async () => {
    if (!addForm.name || !addForm.email || !addForm.password) return;
    setCreating(true);
    try {
      await api.post("/users", addForm);
      toast.success(`${addForm.name} added as ${addForm.role}`);
      setAddOpen(false);
      setAddForm({ name: "", email: "", password: "", role: "user" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const changeRole = async (u, role) => {
    setSaving(u.id);
    try {
      await api.patch(`/users/${u.id}/role`, { role });
      toast.success(`${u.name} is now ${role}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  const openEdit = (u) => { setEditUser(u); setEditForm({ name: u.name, email: u.email, role: u.role }); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${editUser.id}`, editForm);
      toast.success("User updated");
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async () => {
    if (pwValue.length < 6) return;
    setBusy(true);
    try {
      await api.post(`/users/${pwUser.id}/reset-password`, { password: pwValue });
      toast.success(`Password reset for ${pwUser.name}`);
      setPwUser(null);
      setPwValue("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${delUser.id}`);
      toast.success(`${delUser.name} removed`);
      setDelUser(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q ? users.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.role.includes(q)) : users;

  return (
    <div className="space-y-3" data-testid="users-panel-wrap">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email or role" className="w-full pl-9 sm:w-[260px]" data-testid="users-search-input" />
        </div>
        <Button size="sm" className="shine-hover" onClick={() => setAddOpen(true)} data-testid="add-user-button"><Plus className="h-4 w-4" /> Add User</Button>
      </div>
      <div className="panel overflow-hidden md:overflow-x-auto" data-testid="users-panel">
      <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-border text-xs font-mono-label uppercase tracking-widest text-muted-foreground min-w-[760px]">
        <div className="col-span-3">User</div>
        <div className="col-span-3">Email</div>
        <div className="col-span-1">Orders</div>
        <div className="col-span-3">Role</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {loading ? (
        <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="users-empty">No users found</div>
      ) : filtered.map((u) => {
        const isSelf = me?.id === u.id;
        return (
        <div key={u.id} className="grid grid-cols-2 gap-3 px-4 py-4 border-b border-border/60 last:border-0 items-center hover:bg-secondary/30 md:grid-cols-12 md:gap-4 md:px-5 md:min-w-[760px]" data-testid={`user-row-${u.email}`}>
          <div className="col-span-2 md:col-span-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary"><UserCog className={`h-4 w-4 ${ROLE_COLOR[u.role] || "text-muted-foreground"}`} /></div>
            <span className="font-medium text-sm truncate">{u.name}{isSelf && <span className="ml-1 text-[10px] font-mono-label uppercase text-muted-foreground">(you)</span>}</span>
          </div>
          <div className="col-span-2 md:col-span-3 text-sm text-muted-foreground truncate">{u.email}</div>
          <div className="text-sm md:col-span-1"><span className="md:hidden text-muted-foreground">Orders: </span>{u.order_count}</div>
          <div className="md:col-span-3">
            <Select value={u.role} onValueChange={(r) => changeRole(u, r)} disabled={saving === u.id || isSelf}>
              <SelectTrigger className="w-[140px]" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`user-actions-${u.email}`}><MoreHorizontal className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="gap-2 rounded-lg" onClick={() => openEdit(u)} data-testid={`edit-user-btn-${u.email}`}><UserCog className="h-4 w-4 text-primary" /> Edit details</DropdownMenuItem>
                <DropdownMenuItem className="gap-2 rounded-lg" onClick={() => { setPwUser(u); setPwValue(""); }} data-testid={`reset-pw-btn-${u.email}`}><ShieldCheck className="h-4 w-4 text-orange-600" /> Reset password</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2 rounded-lg text-brand-red focus:text-brand-red" disabled={isSelf} onClick={() => setDelUser(u)} data-testid={`delete-user-btn-${u.email}`}><Trash2 className="h-4 w-4" /> Delete user</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ); })}
      </div>

      {/* Add user */}
      <Dialog open={addOpen} onOpenChange={(v) => { if (!v) setAddOpen(false); }}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Add User</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Create a login account and assign its role.</p>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Full Name</Label><Input value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Ramesh Yadav" data-testid="add-user-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} placeholder="person@company.com" data-testid="add-user-email-input" /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={addForm.password} onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))} placeholder="Min 6 characters" data-testid="add-user-password-input" /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={addForm.role} onValueChange={(r) => setAddForm((f) => ({ ...f, role: r }))}>
                <SelectTrigger data-testid="add-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createUser} disabled={creating || !addForm.name || !addForm.email || addForm.password.length < 6} data-testid="confirm-add-user-button">
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user */}
      <Dialog open={!!editUser} onOpenChange={(v) => { if (!v) setEditUser(null); }}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Edit User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Full Name</Label><Input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid="edit-user-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} data-testid="edit-user-email-input" /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={editForm.role} onValueChange={(r) => setEditForm((f) => ({ ...f, role: r }))} disabled={me?.id === editUser?.id}>
                <SelectTrigger data-testid="edit-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
              {me?.id === editUser?.id && <p className="text-xs text-muted-foreground">You cannot change your own role.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} disabled={busy || !editForm.name} data-testid="confirm-edit-user-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!pwUser} onOpenChange={(v) => { if (!v) { setPwUser(null); setPwValue(""); } }}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Reset Password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Set a new password for <span className="font-semibold text-foreground">{pwUser?.name}</span>. Their existing sessions will be signed out.</p>
          <div className="space-y-1.5"><Label>New Password</Label><Input type="password" value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="Min 6 characters" data-testid="reset-pw-input" /></div>
          <DialogFooter>
            <Button onClick={savePassword} disabled={busy || pwValue.length < 6} data-testid="confirm-reset-pw-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Set Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <Dialog open={!!delUser} onOpenChange={(v) => { if (!v) setDelUser(null); }}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Delete User</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Remove <span className="font-semibold text-foreground">{delUser?.name}</span> ({delUser?.email})? This cannot be undone. Users with shipments must be reassigned first.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelUser(null)} data-testid="cancel-delete-user-button">Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={busy} data-testid="confirm-delete-user-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
